import { Children, Fragment, isValidElement } from 'react';
import type { ReactNode } from 'react';

export interface SelectOption {
  value: string;
  label: string;
  disabled: boolean;
  group?: string;
}

function textContent(children: ReactNode): string {
  return Children.toArray(children).map(child => isValidElement<{ children?: ReactNode }>(child)
    ? textContent(child.props.children) : String(child)).join('');
}

export function readOptions(children: ReactNode, group?: string, disabled = false): SelectOption[] {
  const result: SelectOption[] = [];
  Children.forEach(children, child => {
    if (!isValidElement<{ children?: ReactNode; value?: string | number; label?: string; disabled?: boolean; hidden?: boolean }>(child)) return;
    const props = child.props;
    if (child.type === Fragment) result.push(...readOptions(props.children, group, disabled));
    if (child.type === 'optgroup') result.push(...readOptions(props.children, props.label, disabled || !!props.disabled));
    if (child.type === 'option' && !props.hidden) result.push({
      value: String(props.value ?? textContent(props.children)),
      label: props.label ?? textContent(props.children),
      disabled: disabled || !!props.disabled,
      group,
    });
  });
  return result;
}

export function moveOption(options: SelectOption[], from: number, direction: 1 | -1): number {
  for (let index = from + direction; index >= 0 && index < options.length; index += direction) {
    if (!options[index].disabled) return index;
  }
  return from >= 0 && from < options.length && !options[from].disabled ? from : -1;
}

const normalize = (value: string) => value.normalize('NFKD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase();

export function matchOption(options: SelectOption[], query: string, from: number, includeCurrent = false): number {
  if (!query) return -1;
  const needle = normalize(query);
  for (let step = includeCurrent ? 0 : 1; step < options.length + (includeCurrent ? 0 : 1); step++) {
    const index = ((from + step) % options.length + options.length) % options.length;
    if (!options[index].disabled && normalize(options[index].label.trim()).startsWith(needle)) return index;
  }
  return -1;
}

interface Rect { left: number; top: number; bottom: number; width: number }
interface Viewport { left: number; top: number; width: number; height: number }

export function popupPosition(anchor: Rect, viewport: Viewport, contentHeight: number) {
  const gap = 6;
  const margin = 8;
  const leftEdge = viewport.left + margin;
  const topEdge = viewport.top + margin;
  const rightEdge = viewport.left + viewport.width - margin;
  const bottomEdge = viewport.top + viewport.height - margin;
  const width = Math.max(0, Math.min(Math.max(anchor.width, 220), rightEdge - leftEdge));
  const below = Math.max(0, bottomEdge - anchor.bottom - gap);
  const above = Math.max(0, anchor.top - topEdge - gap);
  const down = below >= Math.min(240, contentHeight) || below >= above;
  const maxHeight = Math.min(320, Math.max(0, viewport.height - margin * 2), down ? below : above);
  const height = Math.min(contentHeight, maxHeight);
  return {
    left: Math.max(leftEdge, Math.min(anchor.left, rightEdge - width)),
    top: Math.max(topEdge, Math.min(down ? anchor.bottom + gap : anchor.top - gap - height, bottomEdge - height)),
    width, maxHeight,
  };
}
